<template>
  <div class="distribution-account-management">
    <el-card shadow="never" class="animate__animated animate__fadeInUp">
      <div class="filter-card">
        <el-row :gutter="20">
          <el-col :span="24">
            <div class="search-bar" style="display: flex; align-items: center; flex-wrap: wrap; gap: 10px;">
              <el-input
                v-model="searchUsername"
                placeholder="搜索用户名"
                prefix-icon="el-icon-search"
                clearable
                @keyup.enter.native="handleSearch"
                style="width: 240px;"
              ></el-input>
              <div style="margin-left: auto; display: flex; gap: 10px;">
                <el-button
                  type="primary"
                  icon="el-icon-search"
                  @click="handleSearch"
                >搜索</el-button>
                <el-button
                  type="success"
                  icon="el-icon-plus"
                  @click="resetAndShowAddForm"
                >新增</el-button>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>

      <div v-if="error" class="error">{{ error }}</div>
      <el-empty
        v-else-if="!loading && allAccounts.length === 0"
        description="暂无分销账户数据"
      />

      <div v-else>
        <!-- 分销账户表格 -->
        <el-table
          :data="filteredAccounts"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载账户数据..."
          class="animate__animated animate__fadeIn"
        >
          <el-table-column prop="id" label="ID" width="120" align="center" />
          <el-table-column prop="username" label="用户名" min-width="100" show-overflow-tooltip />
          <el-table-column label="账户状态" min-width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
                {{ scope.row.status === 1 ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="身份权限" min-width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.role === 0 ? '' : 'info'">
                {{ scope.row.role === 0 ? '超级管理员' : '分销管理员' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="180" align="center">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="primary"
                icon="el-icon-edit"
                @click="handleEditAccount(scope.row)"
              >编辑</el-button>
              <el-button
                size="mini"
                type="danger"
                icon="el-icon-delete"
                @click="handleDeleteAccount(scope.row)"
              >删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.pageNum"
            :page-size="pagination.pageSize"
            :page-sizes="[5, 10, 20, 50]"
            :total="totalAccounts"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 添加分销账户对话框 -->
    <el-dialog
      title="添加分销账户"
      :visible.sync="showAddForm"
      width="450px"
      center
    >
      <el-form :model="currentAccount" :rules="accountRules" ref="accountForm" label-width="100px">
        <el-form-item label="用户名" prop="username">
          <el-input 
            v-model="currentAccount.username" 
            placeholder="请输入用户名"
          ></el-input>
        </el-form-item>
        
        <el-form-item label="密码" prop="password">
          <el-input 
            v-model="currentAccount.password" 
            type="password"
            placeholder="请输入密码"
          ></el-input>
        </el-form-item>
        
        <el-form-item label="账户状态" prop="status">
          <el-radio-group v-model="currentAccount.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="身份权限" prop="role">
          <el-radio-group v-model="currentAccount.role">
            <el-radio :label="0">超级管理员</el-radio>
            <el-radio :label="1">分销管理员</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="showAddForm = false">取 消</el-button>
        <el-button type="primary" @click="submitAccountForm">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 编辑分销账户对话框 -->
    <el-dialog
      title="编辑分销账户"
      :visible.sync="showEditForm"
      width="450px"
      center
    >
      <el-form :model="editAccount" :rules="editRules" ref="editForm" label-width="100px">
        <el-form-item label="用户名" prop="username">
          <el-input
            v-model="editAccount.username"
            placeholder="请输入用户名"
          ></el-input>
        </el-form-item>

        <el-form-item label="账户状态" prop="status">
          <el-radio-group v-model="editAccount.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="身份权限" prop="role">
          <el-radio-group v-model="editAccount.role">
            <el-radio :label="0">超级管理员</el-radio>
            <el-radio :label="1">分销管理员</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="showEditForm = false">取 消</el-button>
        <el-button type="primary" @click="submitEditForm">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 删除确认对话框 -->
    <el-dialog
      title="删除分销账户"
      :visible.sync="deleteDialogVisible"
      width="400px"
      center
    >
      <p>警告：此操作将永久删除分销账户 <strong>{{ currentAccount.username }}</strong>！</p>
      <p>该操作不可撤销，所有相关数据也将被删除。</p>
      <p>请确认是否继续执行此操作？</p>
      <div slot="footer" class="dialog-footer">
        <el-button @click="deleteDialogVisible = false">取 消</el-button>
        <el-button type="danger" @click="confirmDeleteAccount">确 定删除</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { request } from '@/utils/http'

export default {
  name: 'DistributionAccountManagement',
  data() {
    return {
      allAccounts: [], // 存储所有账户数据
      loading: false,
      error: '',
      searchUsername: '',
      showAddForm: false,
      showEditForm: false,
      deleteDialogVisible: false,
      currentAccount: {},
      editAccount: {},
      pagination: {
        pageNum: 1,
        pageSize: 10
      }
    }
  },
  computed: {
    filteredAccounts() {
      if (!this.searchUsername) {
        // 如果没有搜索条件，返回当前页的数据
        const startIndex = (this.pagination.pageNum - 1) * this.pagination.pageSize;
        const endIndex = startIndex + this.pagination.pageSize;
        return this.allAccounts.slice(startIndex, endIndex);
      }
      
      // 如果有搜索条件，返回过滤后的数据并分页
      const filtered = this.allAccounts.filter(account => 
        account.username.toLowerCase().includes(this.searchUsername.toLowerCase())
      );
      
      const startIndex = (this.pagination.pageNum - 1) * this.pagination.pageSize;
      const endIndex = startIndex + this.pagination.pageSize;
      return filtered.slice(startIndex, endIndex);
    },
    
    // 总账户数（用于分页）
    totalAccounts() {
      if (!this.searchUsername) {
        return this.allAccounts.length;
      }
      
      return this.allAccounts.filter(account => 
        account.username.toLowerCase().includes(this.searchUsername.toLowerCase())
      ).length;
    },
    
    // 表单验证规则
    accountRules() {
      return {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '用户名长度在3到20个字符', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度在6到20个字符', trigger: 'blur' }
        ],
        status: [
          { required: true, message: '请选择账户状态', trigger: 'change' }
        ],
        role: [
          { required: true, message: '请选择身份权限', trigger: 'change' }
        ]
      };
    },
    editRules() {
      return {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '用户名长度在3到20个字符', trigger: 'blur' }
        ],
        status: [
          { required: true, message: '请选择账户状态', trigger: 'change' }
        ],
        role: [
          { required: true, message: '请选择身份权限', trigger: 'change' }
        ]
      };
    }
  },
  async mounted() {
    await this.fetchAccounts()
  },
  methods: {
    async fetchAccounts() {
      this.loading = true
      this.error = ''
      
      try {
        // 获取所有管理员账号
        const response = await request('/admin/AdminAccount/all', {
          method: 'GET'
        });
        
        if (response.code === 200) {
          this.allAccounts = response.data
        } else {
          throw new Error(response.msg || '获取账户列表失败');
        }
      } catch (error) {
        console.error('获取分销账户列表失败:', error)
        this.error = error.message || '获取分销账户列表失败'
      } finally {
        this.loading = false
      }
    },
    
    handleCurrentChange(pageNum) {
      this.pagination.pageNum = pageNum;
    },
    
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize;
      this.pagination.pageNum = 1;
    },
    
    handleSearch() {
      this.pagination.pageNum = 1; // 搜索时回到第一页
    },
    
    handleEditAccount(account) {
      this.editAccount = {
        id: account.id,
        username: account.username,
        status: account.status,
        role: account.role
      };
      this.showEditForm = true;
    },

    submitEditForm() {
      this.$refs.editForm.validate(async (valid) => {
        if (!valid) {
          return false;
        }

        try {
          const response = await request('/admin/AdminAccount/update', {
            method: 'PUT',
            body: JSON.stringify({
              id: this.editAccount.id,
              username: this.editAccount.username,
              status: this.editAccount.status,
              role: this.editAccount.role
            })
          });

          if (response.code === 200) {
            this.$message.success(response.data || '更新成功');
            this.showEditForm = false;
            this.fetchAccounts();
          } else {
            throw new Error(response.msg || '更新失败');
          }
        } catch (error) {
          this.$message.error(error.message || '更新失败');
        }
      });
    },

    handleDeleteAccount(account) {
      this.currentAccount = account;
      this.deleteDialogVisible = true;
    },
    
    async confirmDeleteAccount() {
      try {
        // 使用新的删除接口，通过路径参数传递用户名
        const response = await request(`/admin/AdminAccount/${this.currentAccount.username}`, {
          method: 'DELETE'
        });
        
        if (response.code === 200) {
          this.$message.success(response.data || '管理员账号删除成功');
          this.deleteDialogVisible = false;
          this.fetchAccounts(); // 刷新列表
        } else {
          throw new Error(response.msg || '删除账户失败');
        }
      } catch (error) {
        this.$message.error(error.message || '删除账户失败');
      }
    },
    
    resetAndShowAddForm() {
      this.currentAccount = {
        username: '',
        password: '',
        status: 1,
        role: 1
      };
      this.showAddForm = true;
    },
    
    submitAccountForm() {
      this.$refs.accountForm.validate(async (valid) => {
        if (!valid) {
          return false;
        }

        try {
          const response = await request('/admin/AdminAccount/add', {
            method: 'POST',
            body: JSON.stringify({
              username: this.currentAccount.username,
              password: this.currentAccount.password,
              status: parseInt(this.currentAccount.status),
              role: parseInt(this.currentAccount.role)
            }),
          });

          if (response.code === 200) {
            this.$message.success(response.data || '管理员账号添加成功');
            this.showAddForm = false;
            this.fetchAccounts();
          } else {
            throw new Error(response.msg || '添加账户失败');
          }
        } catch (error) {
          this.$message.error(error.message || '添加账户失败');
        }
      });
    },
    
  }
}
</script>

<style scoped>
.distribution-account-management {
}

.filter-card {
  margin-bottom: 20px;
}

.error {
  color: #f87171;
  padding: 10px;
  background-color: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 4px;
  margin-bottom: 20px;
}

.el-table {
  margin-bottom: 20px;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
}

.dialog-footer {
  text-align: right;
}
</style>